/*eslint @typescript-eslint/no-unused-vars: 'off'*/
import { Home } from '@mui/icons-material';
import { Chip, Stack, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import useTheme from '@mui/material/styles/useTheme';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DataGridPremium, DataGridPremiumProps, GridCsvGetRowsToExportParams, gridDateComparator, GridExcelExportOptions, gridFilteredSortedRowIdsSelector, GridRenderCellParams, gridRowIdsSelector, gridRowTreeSelector, GridToolbar, useGridApiRef, useKeepGroupedColumnsHidden } from '@mui/x-data-grid-premium';
import { titleize } from 'inflection';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useSearchParams } from 'react-router-dom';
import { AnomalyReportDocument, AnomalyReportEntry, getAnomalyReport } from './reports';
import styles from './styles/Home.module.css';
// import { useDemoData } from '@mui/x-data-grid-generator';
import getYear from 'date-fns/getYear';

export type FlattenedAnomalyReportEntry = AnomalyReportEntry & { _id: string; description: string }; // NB: not the same as ReportParams.reportId

export type ReportParams = { reportId: string };

const logo = '/OG side by side logo.png';

export const Report: FC = () => {
  const { reportId: id } = useParams<ReportParams>();
  const [ urlSearchParams, _setUrlSearchParams ] = useSearchParams();
  const token = useMemo<string | null>( () => urlSearchParams.get( 't' ), [ urlSearchParams ] );
  const theme = useTheme();
  const isXSmall = useMediaQuery( theme.breakpoints.down( 'sm' ) );
  const isSmall = useMediaQuery( theme.breakpoints.down( 'md' ) );

  const [ isLoaded, setIsLoaded ] = useState( false );
  const [ report, setReport ] = useState<AnomalyReportDocument>();
  const [ rowGroupingModel, setRowGroupingModel ] = useState( [ 'description', /* 'locationName' , */ 'practitionerFullName' ] );
  const rowGroupingModelStr = rowGroupingModel.join( '-' );
  const apiRef = useGridApiRef();
  const isFake = !!report?.fake;

  const collapseOrExpandAll = useCallback( ( isExpand: boolean ) => () => {
    const rowIds = gridRowIdsSelector( apiRef );
    for( const rowId of rowIds ) {
      apiRef.current.setRowChildrenExpansion( rowId, isExpand );
    }
  }, [ apiRef ] );

  const initialState = useKeepGroupedColumnsHidden( {
    apiRef,
    initialState: {
      columns: {
        columnVisibilityModel: {
          practitionerFullName: false,
          patientFullName: false,
          patient: false,
          // dob: false,
        },
      },
      rowGrouping: {
        model: rowGroupingModel, //[ 'reportId', 'locationName', 'practitionerFullName' ],
      },
    },
  } );

  useEffect( () => {
    ( async () => {
      if( !id || isLoaded ) return;
      const doc = await getAnomalyReport( id, token );
      if( !doc ) return;
      setIsLoaded( true );
      setReport( doc );
    } )()
  }, [ id, token ] );

  const getRowsWithoutGroups = ( { apiRef }: GridCsvGetRowsToExportParams ) => {
    const rows = gridFilteredSortedRowIdsSelector( apiRef );
    const tree = gridRowTreeSelector( apiRef );
    return rows.filter( ( rowId ) => !tree[ rowId ].isAutoGenerated );
  };

  const exceljsPreProcess: NonNullable<GridExcelExportOptions[ 'exceljsPreProcess' ]> = async ( { workbook, worksheet } ) => {
    if( !report ) return;
    workbook.created = new Date( report.createdAt );
    worksheet.name = report.reportType.slice( 0, 31 ); // Excel won't accept over 31
    worksheet.getCell( 'A1' ).value = `${ report.estimatedDollarLossString.replace( /\.\d\d$/, '' ) } Estimated Revenue Loss (${ report.estimatedValueOfAppointment } per appt)`;
    worksheet.getCell( 'A1' ).style = { font: { bold: true } };
    worksheet.mergeCellsWithoutStyle( 'A1:C1' );
    worksheet.getCell( 'A2' ).value = `${ report.errorPercentageString }% Error Rate Found in ${ report.appointmentsWithErrors } of ${ report.totalAppointmentsAnalyzed } Total Appointments`;
    worksheet.getCell( 'A2' ).style = { font: { bold: true } };
    worksheet.mergeCellsWithoutStyle( 'A2:C2' );
    worksheet.getCell( 'A3' ).value = `Period Covers ${ report.humanAppointmentStartTime } to ${ report.humanAppointmentStopTime }`;
    worksheet.getCell( 'A3' ).style = { font: { bold: true } };
    worksheet.mergeCellsWithoutStyle( 'A3:C3' );
    worksheet.getCell( 'A4' ).value = '';
  }

  const exceljsPostProcess: NonNullable<GridExcelExportOptions[ 'exceljsPostProcess' ]> = async ( { worksheet } ) => {
    if( !report ) return;
    worksheet.getRow( 5 ).font = { bold: true };

  };

  const data = useMemo<DataGridPremiumProps<FlattenedAnomalyReportEntry>>( () => {

    return {
      rows: report?.reports.flatMap( report => report.entry.map( e => ( {
        ...e,
        _id: report._id,
        // reportId: report.reportId,
        description: report.description,
      } ) ) ) || [],

      columns: [
        {
          field: 'description',
          headerName: 'Report ',
          export: true,
        },
        {
          field: 'patientFullName',
          headerName: 'Patient',
          width: 260,
          cellClassName: 'bold-cell',
        },
        {
          field: 'dob',
          headerName: 'DOB',
          cellClassName: 'bold-cell',
          flex: 1.5,
          value: 'date',
          sortComparator: ( v1, v2 ) => Date.parse( v1 ) - Date.parse( v2 ),
          align: 'right',
        },
        {
          field: 'locationName',
          headerName: 'Location',
          valueGetter: ( params ) => {
            const { locationName } = params.row;
            if( !locationName ) return null;
            return locationName.replace( /SummitMD Dermatology:? /, '' );
          },
          groupingValueGetter: ( params ) => {
            const { locationName } = params.row;
            if( !locationName ) return null;
            return 'Location: ' + locationName.replace( /SummitMD Dermatology:? /, '' );
          },
          export: true,
        },
        {
          field: 'practitionerFullName',
          headerName: 'Practitioner',
          //width: 300
          valueGetter: ( params ) => {
            const { practitionerFullName } = params.row;
            if( !practitionerFullName ) return null;
            return practitionerFullName;
          },
          groupingValueGetter: ( params ) => {
            const { practitionerFullName } = params.row;
            if( !practitionerFullName ) return null;
            return 'Practitioner: ' + practitionerFullName;
          },
          export: true,
        },
        {
          field: 'appointment',
          headerName: 'Appointment Date',
          flex: 1.7,
          value: 'date',
          sortComparator: ( v1, v2 ) => Date.parse( v1 ) - Date.parse( v2 ),
          align: 'center',
          valueGetter: ( params ) => {
            const { apptDate } = params.row;
            if( !apptDate ) return null;
            const d = new Date( apptDate );
            return [
              d.toLocaleTimeString( [], { hour: 'numeric', minute: '2-digit' } ),
              d.toLocaleDateString( [], { month: 'short', day: 'numeric' } )
            ].join( ' ' );
          },
        },
        {
          field: 'apptType',
          headerName: 'Type',
          flex: 1.4,
          value: 'singleSelect',
          valueGetter: ( params ) => titleize( params.value || '' ),
          renderCell: ( params: GridRenderCellParams<string> ) => {
            if( !params.value ) return null;
            return (
              <Chip label={params.value} size='small' variant='outlined' />
            );
          },
        },
        {
          field: 'apptStatus',
          headerName: 'Status',
          flex: 1.4,
          value: 'singleSelect',
          valueGetter: ( params ) => titleize( params.value || '' ),
          renderCell: ( params: GridRenderCellParams<string> ) => {
            if( !params.value ) return null;
            return (
              <Chip label={titleize( params.value )} size='small' variant='outlined' />
            );
          },
        },
        {
          field: 'apptFhirId',
          headerName: 'FHIR Id',
          flex: 1,
        },
        {
          field: 'newPatient',
          headerName: 'New Patient',
          flex: 1,
          value: 'boolean',
          renderCell: ( params: GridRenderCellParams<boolean> ) => {
            // if( params.value === undefined || params.value === false ) return <></>;
            if( !params.value ) return <></>;
            return (
              <Chip label='New' size='small' variant='filled' color='success' />
            );
          },
        },
        {
          field: 'reason',
          headerName: 'Reason',
          flex: 2,
          value: 'singleSelect',
        },
      ],
      getRowId: ( r: FlattenedAnomalyReportEntry ) => [ r._id, r.apptFhirId ].join( ',' ),
      rowGroupingColumnMode: 'single',
      groupingColDef: {
        leafField: 'patientFullName',
        hideDescendantCount: true,
      },
      defaultGroupingExpansionDepth: -1,
      getRowClassName: ( params ) => {
        const { id } = params;
        if( typeof id == 'string' ) {
          // 'auto-generated-row-reportId/Booked Appt with Encounter-locationName/Waco'
          if( id.match( /^auto-generated-row-/ ) ) {
            return `report-datagrid--grouped report-datagrid--grouped-${ id.split( '/' ).length - 2 }`;
          }
        }
        return '';
      },
      disableColumnSelector: true,  // remove Columns // https://mui.com/x/react-data-grid/column-visibility/#column-visibility-panel
      componentsProps: {
        toolbar: {
          csvOptions: {
            utf8WithBom: true,
            allColumns: true,
            getRowsToExport: getRowsWithoutGroups,
          },
          excelOptions: {
            allColumns: true,
            getRowsToExport: getRowsWithoutGroups,
            exceljsPreProcess,
            exceljsPostProcess,
          },
          printOptions: {
            // disableToolbarButton: true,
            pageStyle: '.MuiDataGrid-root .MuiDataGrid-main { color: rgba(0, 0, 0, 0.87); }',
            hideToolbar: true,
            hideFooter: true,
          }
        }
      }
    };
  }, [ report ] );

  return (
    <Box sx={{
      padding: { xs: 0, md: '0 1.5rem' },
      maxWidth: '1280px',
      marginLeft: 'auto',
      marginRight: 'auto',
      fontSize: { xs: '1rem', md: '1.2rem' },
      backgroundColor: '#f9fafb',
      // fontFamily: fontBody,

    }}>
      <Helmet>
        <title>
          {report
            ? `${ report.reportIntervalType } ${ report.reportType } - ${ report?.fake ? 'DEMO ' : '' }Anomaly Report - Ending ${ report.humanAppointmentStopTime }`
            : 'Anomaly Report'
          }
        </title>
        <link rel="icon" href="/favicon.ico" />
      </Helmet>

      <main
        //className={styles.main}
        style={{
          fontFamily: undefined, /* fontBody */
          marginLeft: isSmall ? '1em' : undefined,
          marginRight: isSmall ? '1em' : undefined,

        }}
      >

        <Box sx={{ float: 'left', position: 'absolute' }} >
          <Tooltip title='Return to Reports List Page' placement='bottom-end' >
            <Button
              href={`../reports${ token ? `?t=${ token }` : '' }`}
              startIcon={<Home />}
              sx={{
                color: '#5f708f',
              }}
              size='large'
            >
              {!isSmall ? 'Reports' : null}
            </Button>
          </Tooltip>
        </Box>

        {/* <TopSection {...{ location, organization, fontHeader, colorPrimary, isXSmall }} /> */}

        <Grid item key="content">
          {isFake &&
            <Box key='logo'>
              <img src={logo} height={isSmall ? 70 : 100} style={{ margin: '1em auto -1em', display: 'block' }} />
            </Box>
          }
          <Typography variant='h6'
            className={styles.description}
            sx={{
              margin: '1em 0 2em',
              fontWeight: 'bold',
              color: '#5f708f',
            }}
          >
            {report
              ? ( !isSmall
                ? `${ report.reportIntervalType } Anomaly Report: ${ report.reportType }`
                : (
                  <>
                    {report.reportIntervalType} Anomaly Report
                    <br />
                    {report.reportType}
                  </>
                )
              )
              : 'Anomaly Report'
            }
            {report?.fake && (
              <>
                <br />
                <span style={{ fontSize: '80%' }} >
                  &mdash; Anonymized Data &mdash;
                </span>
              </>
            )}

          </Typography>

          {report &&
            <Box
              mb={3}
              sx={{
                fontWeight: 'bold',
              }}
            >
              <Typography sx={{ fontWeight: 'bold' }}>{report.estimatedDollarLossString.replace( /\.\d\d$/, '' )} Estimated Revenue Loss (${report.estimatedValueOfAppointment} per appt)</Typography>
              <Typography sx={{ fontWeight: 'bold' }}> {report.errorPercentageString}% Error Rate Found in {report.appointmentsWithErrors} of {report.totalAppointmentsAnalyzed} Total Appointments</Typography>
              <Typography sx={{ fontWeight: 'bold' }}>Period Covers {report.humanAppointmentStartTime} to {report.humanAppointmentStopTime}</Typography>

              {/* 
                 <Typography sx={{ fontWeight: 'bold' }}>Covering Period From: {report.humanAppointmentStartTime} to {report.humanAppointmentStopTime}</Typography>
                 <Typography sx={{ fontWeight: 'bold' }}>Appointment Errors were Found in {report.appointmentsWithErrors} of {report.totalAppointmentsAnalyzed} Total Appointments</Typography>
                 <Typography sx={{ fontWeight: 'bold' }}> Error Rate: {report.errorPercentageString}%</Typography>
                 <Typography sx={{ fontWeight: 'bold' }}> Estimated Revenue Loss (${ report.estimatedValueOfAppointment } per appt): {report.estimatedDollarLossString}</Typography>
               */}

            </Box>
          }

          <Box
            sx={{
              '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
              //.MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)
              '& .MuiDataGrid-root .report-datagrid--grouped:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell': {
                overflow: 'inherit',
                overflowX: 'visible',
                overflowY: 'visible',
              },
              '& .report-datagrid--grouped-0': {
                color: '#5f708f',
                fontWeight: 'bold',
                backgroundColor: '#f4f2f2',
              },
              '& .report-datagrid--grouped-1': {
                color: '#3C72A5',
              },
              '& .report-datagrid--grouped-2': {
                color: '#B17947',
              },
              '& .bold-cell': {
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-row:not(.report-datagrid--grouped)> .MuiDataGrid-cell[data-colindex="0"]': { // leaf/first cell
                fontWeight: 'bold',
              }
            }}
          >
            <Grid container direction='row' justifyContent='space-between' alignItems='flex-end'>
              <Grid item>
                <Stack
                  sx={{ width: '100%', mb: 1 }}
                  direction="row"
                  alignItems="flex-start"
                  columnGap={1}
                >
                  <Chip
                    label="By Practitioner"
                    onClick={() => setRowGroupingModel( [ 'description', 'practitionerFullName' ] )}
                    variant="outlined"
                    color={rowGroupingModelStr === 'description-practitionerFullName' ? 'primary' : undefined}
                  />
                  <Chip
                    label="By Location"
                    onClick={() => setRowGroupingModel( [ 'description', 'locationName' ] )}
                    variant="outlined"
                    color={rowGroupingModelStr === 'description-locationName' ? 'primary' : undefined}
                  />
                </Stack>
              </Grid>
              <Grid item>
                <Stack
                  sx={{ width: '1 00%', mb: 1 }}
                  direction="row"
                  alignItems="flex-start"
                  columnGap={1}
                >

                  <Button
                    size='small'

                    onClick={collapseOrExpandAll( true )}
                    // variant="outlined"
                    color='primary'
                  // disabled={true}
                  >
                    Expand all
                  </Button>
                  <Button
                    size='small'
                    onClick={collapseOrExpandAll( false )}
                    // variant="outlined"
                    color='primary'
                  >
                    Collapse All
                  </Button>
                </Stack>
              </Grid>
            </Grid>

            <DataGridPremium
              autoHeight
              density='compact'
              {...data}
              apiRef={apiRef}
              loading={!isLoaded}
              disableSelectionOnClick
              initialState={initialState}
              rowGroupingModel={rowGroupingModel}
              components={{
                Toolbar: GridToolbar,
              }}
              hideFooterRowCount
            />

          </Box>

          <footer
            className={styles.footer}
            style={{
              maxWidth: 'inherit',
            }}
          >
            <Typography variant="caption" color="textSecondary"  >
              &copy;
              &nbsp;
              2021-{getYear( new Date() )}
              &nbsp;
              <Link href="https://analoginformatics.com/" target="_blank" rel="noopener" color="inherit" >
                Analog Informatics Corporation
              </Link>
            </Typography>
          </footer>
        </Grid>
      </main >


    </Box >
  )
}

